import { BsPencil } from "react-icons/bs";
import { FiTrash2, FiRefreshCw, FiExternalLink } from "react-icons/fi";
import { FaFileImport } from "react-icons/fa";

import "./ArticleDetails.css";

// Only absolute http(s) links are rendered as links (same rule as the
// backend's ConfiguratorUrlValidator); anything else is shown as plain text.
const safeUrl = (url) => {
    if (typeof url !== "string" || !url.trim()) return null;
    try {
        const u = new URL(url.trim());
        return u.protocol === "http:" || u.protocol === "https:" ? u.href : null;
    } catch {
        return null;
    }
};

// Configurator link: shows the domain + icon, full address on hover.
// No link -> null (the table shows "-").
const renderConfigurator = (url) => {
    const href = safeUrl(url);
    if (!href) return url ? String(url) : null;
    return (
        <a
            className="property-link"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            title={href}
        >
            <span className="property-link__text">{new URL(href).hostname}</span>
            <FiExternalLink />
        </a>
    );
};

const ArticleDetails = ({
    article,
    onEdit,
    onDelete,
    onImport,
    onRefreshFromGlobal, // NEW
    isImported,
    isRefreshing,        // NEW — optional loading state for the button
}) => {


    if (!article) {
        return (
            <div className="article-details empty">
                Select an article
            </div>
        );
    }

    const properties = [
        ["Productgroup", article.name],
        ["Description", article.description],
        ["Manufacturer", article.manufacturer],
        ["Manufacturer Short Name", article.manufacturerShortName],
        ["Type", article.type],
        ["Article Number", article.articleNumber],
        ["Variant", article.variant],
        ["Article Key", article.articleKey],
        ["External Link", renderConfigurator(article.configuratorUrl)],
        ["Record Type", article.recordType],
        ["Makro", article.cadFileName],
        ["Width", article.size?.width],
        ["Height", article.size?.height],
        ["Depth", article.size?.depth],
    ];

    if (article.recordType === "Part" && article.partType === "SubGroup") {
        properties.push(
            ["Device Tag", article.deviceTag],
            ["Carrier Rail", article.carrierRail],

            ["Spacing Left", article.xSpacing?.left],
            ["Spacing Right", article.xSpacing?.right],

            ["Spacing Top", article.ySpacing?.top],
            ["Spacing Bottom", article.ySpacing?.bottom],
        );
    }

    if (article.recordType === "Part" && article.partType === "Enclosure") {
        properties.push(
            ["Cabinet Space", article.space],
            ["Cabinet Clearance", article.insideClearance],
            ["Montage Plate Space",
                article.montageplate?.space],
            ["Montage Plate Distance",
                article.montageplate?.mountingDistance],
            ["Plate Width",
                article.montageplate?.size?.width],
            ["Plate Height",
                article.montageplate?.size?.height],
            ["Plate Depth",
                article.montageplate?.size?.depth]
        );
    }

    // Only a tenant copy that was actually imported from Global has
    // something to refresh against.
    const canRefresh =
        !!article.sourceArticleId && typeof onRefreshFromGlobal === "function";

    const hasActions = onEdit || onDelete || onImport || canRefresh;

    return (
        <div className="article-details">

            <div className="details-toolbar">

                <h3>Properties</h3>

                {hasActions && (
                    <div className="details-actions">

                        {onEdit && (
                            <button
                                className="details-btn edit"
                                onClick={() => onEdit(article)}
                            >
                                <BsPencil />
                                Edit
                            </button>
                        )}

                        {canRefresh && (
                            <button
                                className="details-btn refresh"
                                onClick={() => onRefreshFromGlobal(article)}
                                disabled={isRefreshing}
                                title="Refresh values from Global Library"
                            >
                                <FiRefreshCw className={isRefreshing ? "spin" : ""} />
                                {isRefreshing ? "Refreshing..." : "Refresh"}
                            </button>
                        )}

                        {onDelete && (
                            <button
                                className="details-btn delete"
                                onClick={() => onDelete(article)}
                            >
                                <FiTrash2 />
                                Delete
                            </button>
                        )}

                        {onImport && (
                            <button
                                className="details-btn import"
                                disabled={isImported}
                                onClick={() => onImport(article)}
                            >
                                <FaFileImport />
                                {isImported ? "Imported" : "Import"}
                            </button>
                        )}

                    </div>
                )}

            </div>


            <div className="property-table">

                <div className="property-header">
                    <div>Property</div>
                    <div>Value</div>
                </div>

                {properties.map(([label, value]) => (
                    <div
                        className="property-row"
                        key={label}
                    >
                        <div className="property-name">
                            {label}
                        </div>

                        <div className="property-value">
                            {value ?? "-"}
                        </div>
                    </div>
                ))}

                {Object.entries(article.attributes ?? {}).map(
                    ([key, value]) => (
                        <div
                            key={key}
                            className="property-row"
                        >
                            <div className="property-name">
                                {key}
                            </div>

                            <div className="property-value">
                                {String(value)}
                            </div>
                        </div>
                    )
                )}

            </div>

        </div>
    );
};

export default ArticleDetails;
